import React, { useEffect, useState } from "react";
import { Input } from "reactstrap";
import classnames from "classnames";
import Co2CircleButton from "./Co2CircleButton";

interface NumberInputProps {
    value?: number;
    onChange: (newValue?: number) => void;
    min?: number;
    max?: number;
    disabled?: boolean;
    readOnly?: boolean;
    invalid?: boolean;
    allowInput?: boolean;
    digits2?: boolean;
    placeholder?: string;
}

const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    min = 1,
    max,
    disabled = false,
    readOnly = false,
    invalid = false,
    allowInput = false,
    digits2 = false,
    placeholder,
}) => {
    const [_value, setValue] = useState<number | undefined>(value);
    const [focused, setFocused] = useState<boolean>(false);

    useEffect(() => {
        setValue(value);
    }, [value]);

    const handleIncrement = () => {
        if (_value === undefined) {
            setValue(min);
            onChange(min);
        } else if (max === undefined || _value < max) {
            const newVal = _value + 1;
            setValue(newVal);
            onChange(newVal);
        }
    };

    const handleDecrement = () => {
        if (_value !== undefined && (min === undefined || _value > min)) {
            const newVal = _value - 1;
            setValue(newVal);
            onChange(newVal);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val === "") {
            setValue(undefined);
        } else {
            const parsed = Math.abs(parseInt(val));
            if (!isNaN(parsed)) {
                setValue(parsed);
            }
        }
    };

    const handleBlur = () => {
        setFocused(false);
        onChange(_value);
    };

    return (
        <div
            className={classnames("input-step", {
                "focused-style": focused,
                "border-danger": invalid,
                "form-control-readOnly": readOnly,
            })}
        >
            {!readOnly && (
                <>
                    <div
                        className="co2-field-action-cell d-flex align-items-center justify-content-center"
                        style={{ padding: "0 6px", flexShrink: 0 }}
                    >
                        <Co2CircleButton
                            title="Decrease"
                            aria-label="Decrease"
                            variant="secondary"
                            iconClass="fa-classic fa-solid fa-minus"
                            disabled={disabled || _value === undefined || (min !== undefined && _value <= min)}
                            onClick={handleDecrement}
                        />
                    </div>
                    <div
                        style={{
                            width: 1,
                            alignSelf: "stretch",
                            flexShrink: 0,
                            backgroundColor: "var(--vz-input-field-border)",
                        }}
                    />
                </>
            )}
            <Input
                type="number"
                className="product-quantity w-100"
                style={{
                    padding: 0,
                    outline: "none",
                    boxShadow: "none",
                    border: "0px",
                    fontSize: "0.875rem",
                    textAlign: "center",
                    textAlignLast: "center",
                }}
                value={
                    _value === undefined
                        ? ""
                        : digits2
                            ? _value.toString().padStart(2, "0")
                            : _value
                }
                placeholder={placeholder}
                readOnly={readOnly && !allowInput}
                onChange={(e) => allowInput && !readOnly && handleInputChange(e)}
                onBlur={() => allowInput && !readOnly && handleBlur()}
                onFocus={() => setFocused(true)}
                onKeyDown={(e) => {
                    if (e.key === "-") e.preventDefault();
                }}
            />
            {!readOnly && (
                <>
                    <div
                        style={{
                            width: 1,
                            alignSelf: "stretch",
                            flexShrink: 0,
                            backgroundColor: "var(--vz-input-field-border)",
                        }}
                    />
                    <div
                        className="co2-field-action-cell d-flex align-items-center justify-content-center"
                        style={{ padding: "0 6px", flexShrink: 0 }}
                    >
                        <Co2CircleButton
                            title="Increase"
                            aria-label="Increase"
                            variant="secondary"
                            iconClass="fa-classic fa-solid fa-plus"
                            disabled={disabled || (max !== undefined && _value !== undefined && _value >= max)}
                            onClick={handleIncrement}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default NumberInput;
